import { inject, Injectable } from '@angular/core';
import { FirestoreService } from '../../firestore.service';
import { BehaviorSubject, map } from 'rxjs';
import {
  CreateExerciseParams,
  Exercise, ExercisesImportResult,
} from './exercises-collection.models';
import { FIRESTORE_COLLECTIONS } from '../../firestore.constants';
import { collection, documentId, getDocs, query, Timestamp } from '@firebase/firestore';
import { CountersCollectionService } from '../counters/counters-collection.service';
import { COUNTER_DOCUMENT_IDS } from '../counters/counters-collection.constants';
import { getExerciseThumbnailUrl } from '../../../../shared/utils/exercise.utils';
import { omitFirestoreFields } from '../../firestore.utils';
import { chunk } from '../../../../shared/utils/array.utils';
import { cloneObject } from '../../../../shared/utils/object.utils';
import { EXERCISE_FIELDS, EXERCISE_IMPORT_RESULT_DEFAULT } from './exercises-collection.constants';
import { AuthService } from '../../../auth/auth.service';
import { QueryFilter } from '../../firestore.models';
import { StatusModalService } from '../../../../shared/services/status-modal/status-modal.service';

@Injectable({
  providedIn: 'root',
})
export class ExercisesCollectionService {
  private _fetchedAll = false;
  private readonly _firestoreService = inject(FirestoreService);
  private readonly _countersCollectionService = inject(CountersCollectionService);
  private readonly _authService = inject(AuthService);
  private readonly _statusModalService = inject(StatusModalService);
  private readonly _exercisesMapSubject = new BehaviorSubject(new Map<string, Exercise>());

  public readonly exercises$ = this._exercisesMapSubject.asObservable().pipe(
    map(() => this.exercises),
  );

  private get _exercisesMap(): Map<string, Exercise> {
    return this._exercisesMapSubject.value;
  }

  private set _exercisesMap(exercisesMap: Map<string, Exercise>) {
    this._exercisesMapSubject.next(exercisesMap);
  }

  public get exercises(): Exercise[] {
    return Array.from(this._exercisesMap.values());
  }

  public isExerciseNameExists(name: string, docId?: string): Promise<boolean> {
    const filters: QueryFilter<Exercise>[] = [
      { field: 'exerciseName', operator: '==', value: name },
      { field: 'isDeleted', operator: '==', value: false }
    ];

    if (docId) {
      filters.push({ field: documentId(), operator: '!=', value: docId });
    }

    return this._firestoreService.isExists<Exercise>({
      collectionName: FIRESTORE_COLLECTIONS.EXERCISES,
      filters: filters,
    });
  }

  public getExerciseFromCache(documentId: string): Exercise | undefined {
    return this._exercisesMap.get(documentId);
  }

  public async getExercise(id: string): Promise<Exercise | undefined> {
    const exercisesMap = this._exercisesMap;
    if (exercisesMap.has(id)) return exercisesMap.get(id);

    const exercises = await this._firestoreService.getDocument<Exercise>({
      collectionName: FIRESTORE_COLLECTIONS.EXERCISES,
      documentId: id,
    });

    if (exercises) this._exercisesMap = this._exercisesMap.set(exercises.id!, exercises);
    return exercises;
  }

  public async getExercisesByIds(exerciseIds: string[]): Promise<Exercise[]> {
    const notFoundExercises: string[] = [];
    const exercisesMap = this._exercisesMap;
    exerciseIds.forEach((id) => {
      if (!exercisesMap.has(id)) notFoundExercises.push(id);
    });

    if (notFoundExercises.length === 0) {
      return exerciseIds.map((exerciseId) => exercisesMap.get(exerciseId)!);
    }

    const exercises = await this._firestoreService.getDocuments<Exercise>({
      collectionName: FIRESTORE_COLLECTIONS.EXERCISES,
      filters: [{ field: documentId(), operator: 'in', value: notFoundExercises }],
    });

    exercises.forEach((exercise) => exercisesMap.set(exercise.id!, exercise));
    this._exercisesMap = exercisesMap;

    return exerciseIds.map((exerciseId) => exercisesMap.get(exerciseId)!);
  }

  public async getExercises(): Promise<Exercise[]> {
    const exercisesMap = this._exercisesMap;
    if (this._fetchedAll) return this.exercises;

    const exercises = await this._firestoreService.getDocuments<Exercise>({
      collectionName: FIRESTORE_COLLECTIONS.EXERCISES,
      filters: [{ field: 'isDeleted', operator: '==', value: false }],
    });

    exercises.forEach((exercise) => exercisesMap.set(exercise.id!, exercise));
    this._exercisesMap = exercisesMap;
    this._fetchedAll = true;

    return exercises;
  }

  public async createExercise(exercise: CreateExerciseParams): Promise<Exercise> {
    const counter = await this._countersCollectionService.incrementCount(COUNTER_DOCUMENT_IDS.EXERCISES);
    const newExercise = await this._firestoreService.createDocument<Exercise>({
      collectionName: FIRESTORE_COLLECTIONS.EXERCISES,
      data: {
        ...exercise,
        exerciseNo: counter.count,
        isDeleted: false,
        deletedBy: null,
        dateTimeUpdated: Timestamp.now(),
        thumbnailUrl: getExerciseThumbnailUrl(exercise.videoLink),
      },
    });

    this._exercisesMap = this._exercisesMap.set(newExercise.id!, newExercise);
    return newExercise;
  }

  public async updateExercise(exercise: Exercise): Promise<Exercise> {
    await this._firestoreService.updateDocument<Exercise>({
      collectionName: FIRESTORE_COLLECTIONS.EXERCISES,
      id: exercise.id!,
      data: {
        ...omitFirestoreFields(exercise),
        dateTimeUpdated: Timestamp.now(),
      },
    });

    const exercisesMap = this._exercisesMap;
    const updatedExercise = { ...exercisesMap.get(exercise.id!)!, ...exercise };
    this._exercisesMap = this._exercisesMap.set(exercise.id!, updatedExercise);

    return updatedExercise;
  }

  public async deleteExercise(id: string): Promise<void> {
    await this._firestoreService.updateDocument<Exercise>({
      collectionName: FIRESTORE_COLLECTIONS.EXERCISES,
      id,
      data: {
        isDeleted: true,
        deletedBy: this._authService.userId!,
        dateTimeDeleted: Timestamp.now(),
      }
    });

    const exercisesMap = this._exercisesMap;
    exercisesMap.delete(id);
    this._exercisesMapSubject.next(exercisesMap);
  }

  public async importUpdatedExercises(exercises: Exercise[]): Promise<ExercisesImportResult> {
    const exercisesImportResult = cloneObject(EXERCISE_IMPORT_RESULT_DEFAULT);

    if (exercises.length === 0) return exercisesImportResult;

    try {
      const exercisesMap = this._exercisesMap;
      const exerciseChunk = chunk(exercises, 450);
      for (const chunkExercises of exerciseChunk) {
        const exercisesCommited: Exercise[] = [];
        const batch = this._firestoreService.batch();
        for (const exercise of chunkExercises) {
          if (await this.isExerciseNameExists(exercise.exerciseName, exercise.id!)) {
            exercisesImportResult.failed.push({
              exercise: exercise,
              reason: 'Exercise name already exists',
            });
            continue;
          }

          const ogExercise = exercisesMap.get(exercise.id!) ?? {};
          exercisesMap.set(exercise.id!, { ...ogExercise, ...exercise });
          const documentRef = this._firestoreService.documentRef(
            FIRESTORE_COLLECTIONS.EXERCISES,
            exercise.id!,
          );
          batch.update(documentRef, {
            ...omitFirestoreFields(exercise),
          });
          exercisesCommited.push(exercise);
        }

        await batch.commit();
        exercisesImportResult.success.push(...exercisesCommited);
      }

      this._exercisesMapSubject.next(exercisesMap);
    }
    catch (error) {
      exercisesImportResult.exceptionThrown = true;
      exercisesImportResult.exception = error;
    }

    if (exercisesImportResult.failed.length > 0) {
      exercisesImportResult.exceptionThrown = true;
      exercisesImportResult.exception = new Error(
        `Failed to import ${exercisesImportResult.failed.length} exercises`,
      );
    }
    return exercisesImportResult;
  }

  public async importNewExercises(exercises: Exercise[]): Promise<ExercisesImportResult> {
    const exercisesImportResult = cloneObject(EXERCISE_IMPORT_RESULT_DEFAULT);

    if (exercises.length === 0) return exercisesImportResult;

    try {
      const exercisesMap = this._exercisesMap;
      const exerciseChunks = chunk(exercises, 10);
      for (const exerciseChunk of exerciseChunks) {
        const exerciseNums = exerciseChunk
          .filter((f) => f.exerciseNo !== undefined)
          .map((m) => m.exerciseNo);
        const exerciseNoUsedSet = new Set(
          (
            await this._firestoreService.getDocuments<Exercise>({
              collectionName: FIRESTORE_COLLECTIONS.EXERCISES,
              filters: [{ field: 'exerciseNo', operator: 'in', value: exerciseNums }],
              limit: 10,
            })
          ).map((m) => m.exerciseNo),
        );

        for (const exercise of exerciseChunk) {
          if (await this.isExerciseNameExists(exercise.exerciseName)) {
            exercisesImportResult.failed.push({
              exercise: exercise,
              reason: 'Exercise name already exists',
            });
            continue;
          }

          if (exerciseNoUsedSet.has(exercise.exerciseNo)) {
            exercise.exerciseNo = (
              await this._countersCollectionService.incrementCount(COUNTER_DOCUMENT_IDS.EXERCISES)
            ).count;
          }

          const exerciseResult = await this._firestoreService.createDocument<Exercise>({
            collectionName: FIRESTORE_COLLECTIONS.EXERCISES,
            data: {
              ...omitFirestoreFields(exercise),
              isDeleted: false,
              deletedBy: null,
              dateTimeUpdated: Timestamp.now(),
              thumbnailUrl: getExerciseThumbnailUrl(exercise.videoLink),
            },
          });

          exercisesImportResult.success.push(exerciseResult);
          exercisesMap.set(exerciseResult.id!, exerciseResult);
        }
      }

      this._exercisesMapSubject.next(exercisesMap);
    } catch (error) {
      exercisesImportResult.exceptionThrown = true;
      exercisesImportResult.exception = error;
    }

    if (exercisesImportResult.failed.length > 0) {
      exercisesImportResult.exceptionThrown = true;
      exercisesImportResult.exception = new Error(
        `Failed to import ${exercisesImportResult.failed.length} exercises`,
      );
    }
    return exercisesImportResult;
  }

  public async migrateNewExerciseThumbnailUrl() {
    await this._statusModalService.fire({
      title: 'Migrating Exercises Collection',
      requireConfirmation: false,
      showRetry: false,
      action: async () => {
        const querySnapshot = await getDocs(
          query(collection(this._firestoreService.firestore, FIRESTORE_COLLECTIONS.EXERCISES)),
        );

        const docChunks = chunk(querySnapshot.docs, 500);
        const totalDocs = querySnapshot.docs.length;
        let processedCount = 0;

        for (const docChunk of docChunks) {
          const batch = this._firestoreService.batch();

          for (const docSnapshot of docChunk) {
            processedCount++;
            const exercise = docSnapshot.data() as Exercise;
            const videoLink = exercise[EXERCISE_FIELDS.VIDEO_LINK];
            const thumbnailUrl = exercise[EXERCISE_FIELDS.THUMBNAIL_URL];

            if (!videoLink) {
              console.log(
                `[Progress] (${processedCount}/${totalDocs}) skipping ${docSnapshot.id}, videoLink is empty/null.`,
                `videoLink: ${videoLink}`,
              );
              continue;
            }

            const newThumbnailUrl = getExerciseThumbnailUrl(videoLink);
            if (thumbnailUrl === newThumbnailUrl) {
              console.log(
                `[Progress] (${processedCount}/${totalDocs}) skipping ${docSnapshot.id}, already migrated.`,
                `thumbnailUrl: ${thumbnailUrl}`,
              );
              continue;
            }

            console.log(
              `[Progress] (${processedCount}/${totalDocs}) updated ${thumbnailUrl} -> ${newThumbnailUrl}`,
            );

            batch.update(docSnapshot.ref, {
              [EXERCISE_FIELDS.THUMBNAIL_URL]: newThumbnailUrl,
            });
          }

          await batch.commit();
        }
      },
      onFailed: (err) => console.log(err),
      success: {
        title: 'Migrating Exercises Collection',
        message: 'Successfully Migrated Exercises Collection!',
      },
      failed: {
        title: 'Migrating Exercises Collection',
        message: 'Failed to Migrate Exercises Collection!',
      },
    });
  }
}
